/**
 * Shared image utilities.
 *
 * `downscaleImageToDataURL` rasterises a user-selected image File onto a
 * canvas capped at `maxEdge` pixels on the longest side and re-encodes as
 * JPEG (or the requested mime). The point is to keep base64 DataURLs small
 * enough to fit in a normal JSON request body — phone-camera / Dropbox
 * photos are routinely 5–15 MB raw, which becomes 7–20 MB after base64
 * and is rejected by the ingress / proxy in front of FastAPI (the request
 * fails with NO HTTP status, surfacing as "(failed)" in DevTools).
 *
 * For a 12 MP input (4032 × 3024) capped at maxEdge=1600 + JPEG 0.85, the
 * resulting base64 lands at ~500 KB — safe for any reasonable body limit.
 *
 * If a caller genuinely needs full resolution (measurement tools, takeoff,
 * print export), keep using raw FileReader.readAsDataURL — this util is
 * specifically for any path that ships the DataURL over the network or
 * stores it on a document.
 */

export const DEFAULT_MAX_EDGE = 1600;
export const DEFAULT_JPEG_QUALITY = 0.85;

/**
 * @param {File|Blob} file
 * @param {object} [opts]
 * @param {number} [opts.maxEdge=1600]  Longest-edge cap in pixels.
 * @param {number} [opts.quality=0.85]  JPEG quality (0..1). Ignored for PNG.
 * @param {'image/jpeg'|'image/png'|'image/webp'} [opts.mime='image/jpeg']
 * @returns {Promise<string>} DataURL
 */
export function downscaleImageToDataURL(file, opts = {}) {
  const {
    maxEdge = DEFAULT_MAX_EDGE,
    quality = DEFAULT_JPEG_QUALITY,
    mime = 'image/jpeg',
  } = opts;

  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('no-file'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('read-failed'));
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = () => reject(new Error('image-decode-failed'));
      img.onload = () => {
        try {
          const longest = Math.max(img.width, img.height);
          const scale = longest > maxEdge ? maxEdge / longest : 1;
          const w = Math.max(1, Math.round(img.width * scale));
          const h = Math.max(1, Math.round(img.height * scale));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL(mime, quality));
        } catch (e) {
          reject(e);
        }
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Convenience wrapper that mirrors the legacy FileReader.readAsDataURL
 * callback shape so call sites can drop in with minimal restructure.
 *
 *   reader.onload = (ev) => doStuff(ev.target.result);
 *   reader.readAsDataURL(file);
 *
 * becomes
 *
 *   readImageAsDownscaledDataURL(file, (dataUrl) => doStuff(dataUrl));
 */
export function readImageAsDownscaledDataURL(file, onSuccess, onError, opts) {
  downscaleImageToDataURL(file, opts).then(onSuccess).catch((err) => {
    if (onError) onError(err);
  });
}
